import React, { useEffect, useState } from 'react'
import MainLayout from '../../components/layout/MainLayout'
import configService from '../../services/configService'
import { useDialog } from '@/hooks/useDialog'

interface SystemConfig {
  // Tax Configuration (IRPF)
  irpf_porcentaje?: string
  irpf_minimo_imponible_usd?: string
  irpf_minimo_imponible?: string
  
  // Default Values
  default_iva?: string
  default_comision_propietario?: string
  moneda_default?: string
}

const Settings: React.FC = () => {
  const { showConfirm } = useDialog()
  const [config, setConfig] = useState<SystemConfig>({})
  const [originalConfig, setOriginalConfig] = useState<SystemConfig>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [recalculating, setRecalculating] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  const loadConfigs = async () => {
    setLoading(true)
    try {
      const res = await configService.getAll()
      const items = res.data?.items || []
      
      // Convert array of {clave, valor} to object
      const configObj: SystemConfig = {}
      items.forEach((item: any) => {
        configObj[item.clave as keyof SystemConfig] = item.valor
      })
      
      setConfig(configObj)
      setOriginalConfig(configObj)
      setHasChanges(false)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar configuraciones')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfigs()
  }, [])

  useEffect(() => {
    // Check if there are unsaved changes
    const changed = JSON.stringify(config) !== JSON.stringify(originalConfig)
    setHasChanges(changed)
  }, [config, originalConfig])

  const handleChange = (key: keyof SystemConfig, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setMessage('')

    try {
      // Update each changed configuration
      const updates = Object.entries(config).filter(([key, value]) => {
        return value !== originalConfig[key as keyof SystemConfig]
      })

      // Set USD first so other IRPF updates can recalculate USD contracts.
      updates.sort(([a], [b]) => Number(b === 'irpf_minimo_imponible_usd') - Number(a === 'irpf_minimo_imponible_usd'))
      for (const [key, value] of updates) {
        await configService.update(key, { valor: value || '' })
      }

      setMessage('Configuración guardada. Los cambios de IRPF se aplicaron a los pagos futuros pendientes sin movimientos.')
      setOriginalConfig(config)
      setHasChanges(false)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar configuración')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setConfig(originalConfig)
    setHasChanges(false)
    setError('')
    setMessage('')
  }

  const handleRecalculateIrpf = async () => {
    const confirmed = await showConfirm({
      title: 'Recalcular IRPF',
      message: '¿Está seguro de recalcular IRPF para todos los pagos futuros pendientes?\n\nEsta acción actualizará los montos de los pagos a propietarios según la configuración actual de IRPF (porcentaje y mínimo imponible).',
      type: 'warning',
      confirmText: 'Sí, recalcular',
    });
    if (!confirmed) return;

    setRecalculating(true)
    setError('')
    setMessage('')

    try {
      const result = await configService.recalculateIrpf()
      const data = result.data
      setMessage(
        `IRPF recalculado exitosamente:\n` +
        `• ${data.updated} pagos actualizados\n` +
        `• ${data.skipped} sin cambios\n` +
        `• Configuración aplicada: ${data.current_config.irpf_percentage}% / ${data.current_config.irpf_minimum} UYU / ${data.current_config.irpf_minimum_usd ?? 'sin configurar'} USD`
      )
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al recalcular IRPF')
    } finally {
      setRecalculating(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando configuración...</div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Configuración del Sistema</h2>
          {hasChanges && (
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded whitespace-pre-line">
            {error}
          </div>
        )}

        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded whitespace-pre-line">
            {message}
          </div>
        )}

        {/* Tax Configuration (IRPF) */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-2 text-amber-900">Configuración de IRPF (Impuesto a la Renta)</h3>
          <p className="text-sm text-amber-700 mb-4">
            Estos valores cambian anualmente según la regulación fiscal de Uruguay. 
            Actualice estos valores al inicio de cada año fiscal.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Porcentaje de IRPF (%)
              </label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={config.irpf_porcentaje || ''}
                onChange={(e) => handleChange('irpf_porcentaje', e.target.value)}
                placeholder="10.5"
              />
              <p className="text-xs text-gray-500 mt-1">Ej: 10.5 para 10.5%</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mínimo Imponible (UYU)
              </label>
              <input
                type="number"
                step="1"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={config.irpf_minimo_imponible || ''}
                onChange={(e) => handleChange('irpf_minimo_imponible', e.target.value)}
                placeholder="21290"
              />
              <p className="text-xs text-gray-500 mt-1">Se compara con la porción mensual de cada propietario; la igualdad aplica retención.</p>
            </div>
            <div>
              <label htmlFor="irpf_minimo_imponible_usd" className="block text-sm font-medium text-gray-700 mb-1">
                Mínimo imponible IRPF en USD
              </label>
              <input id="irpf_minimo_imponible_usd" type="number" min="0" step="0.01"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={config.irpf_minimo_imponible_usd ?? ''}
                onChange={(e) => handleChange('irpf_minimo_imponible_usd', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Independiente del mínimo en pesos. Requerido para el cálculo automático de alquileres en dólares.</p>
            </div>
          </div>

          <div className="bg-amber-100 border border-amber-300 rounded p-4">
            <p className="text-sm text-amber-800 mb-3">
              <strong>⚠️ Importante:</strong> Al guardar valores de IRPF se recalculan los pagos futuros pendientes sin movimientos. También puede ejecutar el recálculo manual.
            </p>
            <button
              onClick={handleRecalculateIrpf}
              disabled={recalculating || hasChanges}
              className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {recalculating ? 'Recalculando...' : 'Recalcular IRPF para Pagos Futuros'}
            </button>
            {hasChanges && (
              <p className="text-xs text-amber-700 mt-2">
                Guarde los cambios primero antes de recalcular
              </p>
            )}
          </div>
        </div>

        {/* Default Values */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Valores por Defecto</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IVA por Defecto (%)
              </label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={config.default_iva || ''}
                onChange={(e) => handleChange('default_iva', e.target.value)}
                placeholder="22.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comisión Propietario (%)
              </label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={config.default_comision_propietario || ''}
                onChange={(e) => handleChange('default_comision_propietario', e.target.value)}
                placeholder="10.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Moneda por Defecto
              </label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={config.moneda_default || 'UYU'}
                onChange={(e) => handleChange('moneda_default', e.target.value)}
              >
                <option value="UYU">UYU (Peso Uruguayo)</option>
                <option value="USD">USD (Dólar)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Help Information */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <h3 className="font-semibold text-blue-800 mb-2">ℹ️ Información</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <p>• Todos los cambios se guardan al hacer clic en "Guardar Cambios"</p>
            <p>• Los cambios en IRPF requieren recalcular los pagos futuros para aplicarse</p>
            <p>• Los valores por defecto se aplican a nuevos contratos</p>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

export default Settings




